import { getCookie } from '../utils/cookie';

import request, { apiURL, requestToDownload } from '../utils/api';
import { dissocPath, pathOr } from 'ramda';
class ApplicantFormService {
  getApplicantInformation(
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/shortForms/${applicantId}`,
      options,
    );
  }
  save(applicant: any, organizationId: string, propertyId: string) {
    const applicantIdPath = ['formData', 'applicantInfo', 'applicantId'];
    const applicationIdPath = ['formData', 'applicantInfo', 'applicationId'];
    const applicantId = pathOr('', applicantIdPath, applicant);
    const payload = dissocPath(
      applicationIdPath,
      dissocPath(applicantIdPath, applicant),
    );

    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/shortForms/${applicantId}`,
      options,
    );
  }
  getApplicantPrimaryFormInformation(
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      // Completion state has to come from the server every time. The profile
      // reaches this form by a full page navigation and returns through
      // history, and on a history navigation the browser defaults every
      // request to force-cache — it replays the stored response and ignores
      // `Cache-Control: no-cache`, so an application completed seconds ago
      // still reads as incomplete.
      cache: 'no-store',
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/primaryForms/${applicantId}`,
      options,
    );
  }
  savePrimaryForm(
    applicant: any,
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(applicant),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/primaryForms/${applicantId}`,
      options,
    );
  }
  getApplicantFormData(
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/applicant/${applicantId}/applicant-form`,
      options,
    );
  }
  getApplicantSecondaryFormInformation(
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      // See getApplicantPrimaryFormInformation: a history navigation replays
      // the cached response unless the request opts out of the cache.
      cache: 'no-store',
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/secondaryForms/${applicantId}`,
      options,
    );
  }
  saveSecondaryForm(
    applicant: any,
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(applicant),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/secondaryForms/${applicantId}`,
      options,
    );
  }

  downloadForm(
    organizationId: string,
    propertyId: string,
    applicantId: string,
    includeIncomeAndAssets: boolean,
    isPrimary?: boolean,
    isSecondary?: boolean,
  ) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
      },
    };

    const formType = isPrimary
      ? 'primaryForms'
      : isSecondary
      ? 'secondaryForms'
      : 'shortForms';
    const params = `?includeIncomeAndAssets=${
      includeIncomeAndAssets ? 'true' : 'false'
    }`;
    return requestToDownload(
      `${apiURL}/${organizationId}/${propertyId}/${formType}/${applicantId}/download-form${params}`,
      options,
    );
  }

  getApplicantShortFormInformation(
    applicantId: string,
    organizationId: string,
    propertyId: string,
  ) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      // See getApplicantPrimaryFormInformation: a history navigation replays
      // the cached response unless the request opts out of the cache.
      cache: 'no-store',
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/shortForms/${applicantId}`,
      options,
    );
  }
}

export default ApplicantFormService;
